import { useState } from "react";
import { toast } from "sonner";
import { Receipt, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export const billingComplete = (u) => !!(u?.vat_number && (u?.sdi_code || u?.pec_email));

export default function BillingDataPanel({ user, onSaved }) {
  const vatLocked = !!user?.vat_number;
  const [form, setForm] = useState({
    vat_number: "", fiscal_code: user?.fiscal_code || "",
    sdi_code: user?.sdi_code || "", pec_email: user?.pec_email || "", address: user?.address || "",
  });
  const [busy, setBusy] = useState(false);
  const complete = billingComplete(user);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!vatLocked && !/^\d{11}$/.test(form.vat_number)) return toast.error("Partita IVA non valida (11 cifre)");
    if (!form.sdi_code.trim() && !form.pec_email.trim()) return toast.error("Inserisci il Codice Univoco SDI oppure la PEC");
    if (form.sdi_code.trim() && form.sdi_code.trim().length !== 7) return toast.error("Il Codice SDI deve avere 7 caratteri");
    setBusy(true);
    try {
      const payload = { ...form, sdi_code: form.sdi_code.trim().toUpperCase(), pec_email: form.pec_email.trim() };
      if (vatLocked) delete payload.vat_number;
      await api.patch("/auth/me", payload);
      toast.success("Dati di fatturazione salvati");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`mt-4 ga-card p-4 border ${complete ? "border-slate-200" : "border-amber-300 bg-amber-50/40"}`} data-testid="billing-data-card">
      <div className="flex flex-wrap items-center gap-2">
        <Receipt className="w-4 h-4 text-slate-700" />
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Dati di fatturazione</div>
        {complete ? (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700" data-testid="billing-complete-badge"><CheckCircle2 className="w-3 h-3" /> Completi</span>
        ) : (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-amber-700" data-testid="billing-incomplete-badge"><AlertTriangle className="w-3 h-3" /> Incompleti</span>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-600">Obbligatori per la fattura: <b>Partita IVA</b> e <b>Codice Univoco SDI</b> oppure <b>PEC</b>.</p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div><Label htmlFor="bill-vat">Partita IVA *</Label>{vatLocked ? (<><Input id="bill-vat" value={user.vat_number} readOnly disabled data-testid="billing-vat-input" /><p className="mt-1 text-xs text-slate-500">Non modificabile: per correggerla contatta l'amministratore.</p></>) : (<><Input id="bill-vat" inputMode="numeric" maxLength={11} value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value.replace(/\D/g, "") })} placeholder="12345678901" data-testid="billing-vat-input" /><p className="mt-1 text-xs text-slate-500">Controllala bene: una volta salvata non si potrà più modificare.</p></>)}</div>
        <div><Label htmlFor="bill-sdi">Codice Univoco SDI</Label><Input id="bill-sdi" maxLength={7} value={form.sdi_code} onChange={e => setForm({ ...form, sdi_code: e.target.value.toUpperCase() })} placeholder="ABCDE12" data-testid="billing-sdi-input" /></div>
        <div><Label htmlFor="bill-pec">PEC</Label><Input id="bill-pec" type="email" value={form.pec_email} onChange={set("pec_email")} placeholder="azienda@pec.it" data-testid="billing-pec-input" /></div>
        <div><Label htmlFor="bill-fc">Codice fiscale</Label><Input id="bill-fc" value={form.fiscal_code} onChange={e => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })} placeholder="RSSMRA80A01H501U" data-testid="billing-fc-input" /></div>
        <div className="sm:col-span-2"><Label htmlFor="bill-addr">Sede legale</Label><Input id="bill-addr" value={form.address} onChange={set("address")} placeholder="Via Roma 1, 00100 Roma" data-testid="billing-address-input" /></div>
      </div>
      <Button onClick={save} disabled={busy} className="mt-3 ga-btn-primary rounded-full" data-testid="billing-save-button">{busy ? "Salvataggio..." : "Salva dati di fatturazione"}</Button>
    </div>
  );
}
